/**
 * ENT-4771: boot-time record of a GoTrue implicit-grant SSO callback that has
 * not yet been admitted by `sso-claim-membership`.
 *
 * ENT-4508 makes an `sso/saml` session unusable (PostgREST/Edge `28000
 * session_revoked`) until the canonical claim writes its
 * `sso_session_admissions` row. ENT-4713 withheld the callback session from
 * root providers, but keyed that on the `?sso_return=1` query marker the app's
 * own `/auth` flow appends. A SAML callback can reach the app WITHOUT that
 * marker — an IdP-initiated login (Okta app tile → GoTrue falls back to the
 * Site URL) or any SP-initiated `redirect_to` that is not the app's `/auth`
 * controller — and then the unadmitted bearer was published immediately, so
 * profile/toast reads failed with `session_revoked` before the claim admitted
 * it (staging SSO lanes, 2026-09-21 →).
 *
 * The reliable signal is the callback itself: GoTrue's implicit grant returns
 * the session in the URL fragment (`#access_token=…`). supabase-js consumes and
 * clears that fragment asynchronously (after a network round-trip), so this
 * module snapshots it synchronously at bundle evaluation. The pending record is
 * bound to that exact session and cleared once the claim-admitted session is
 * published, so later auth events (token refresh, tab re-focus) and ordinary
 * reloads of an established session are never deferred.
 */

import { decodeJwt } from 'jose';

import { peekStepUpReassertionMarker, safeStepUpResumeTarget } from '@/lib/ssoReassertion';
import { isSsoAmr } from '@/shared/ssoSession';

interface PendingCallback {
  accessToken: string;
  sessionId: string | null;
}

interface SessionLike {
  access_token?: string | null;
}

let pending: PendingCallback | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

/** GoTrue `session_id` claim of an access token (refresh-stable per session), or null. */
export function readAccessTokenSessionId(accessToken: string): string | null {
  try {
    const claims = decodeJwt(accessToken) as { session_id?: unknown };
    return typeof claims.session_id === 'string' && claims.session_id ? claims.session_id : null;
  } catch {
    return null;
  }
}

/** `iat` (issued-at, seconds) of an access token, or null — orders refreshes of one session. */
export function readAccessTokenIssuedAt(accessToken: string): number | null {
  try {
    const { iat } = decodeJwt(accessToken);
    return typeof iat === 'number' && Number.isFinite(iat) ? iat : null;
  } catch {
    return null;
  }
}

function isSamlAccessToken(accessToken: string): boolean {
  try {
    return isSsoAmr((decodeJwt(accessToken) as { amr?: unknown }).amr);
  } catch {
    return false;
  }
}

/**
 * A SAML step-up re-assertion return (ENT-4204) is identified by BOTH the
 * one-shot marker armed before the IdP redirect AND a valid `stepup_resume`
 * return on the `/?sso_return=1` landing. The marker alone is not enough: an
 * abandoned step-up redirect leaves it in sessionStorage, and a later
 * marker-free IdP-initiated login in that tab must still be treated as an
 * initial, unadmitted callback (Codex P2, PR #3256).
 */
export function isStepUpReassertionReturn(search: string): boolean {
  if (peekStepUpReassertionMarker() === null) {
    return false;
  }
  const params = new URLSearchParams(search);
  return params.get('sso_return') === '1'
    && safeStepUpResumeTarget(params.get('stepup_resume')) !== null;
}

export interface SsoCallbackLocation {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * Record the SSO callback carried by `location.hash`. Non-SSO fragments
 * (password recovery, magic link, email confirmation), error fragments and
 * step-up re-assertion returns are ignored.
 *
 * Returns the URL the page must be moved to before routing, or null. Only the
 * root Index route runs the canonical claim (sso-claim-membership →
 * publishAdmittedSession), so an initial SAML callback that GoTrue delivered to
 * any other path (an allow-listed deep-link `redirect_to`) is re-homed to
 * `/?sso_return=1&return_to=<original path>` with its fragment intact; Index
 * then claims, and honours `return_to` only via `safeReturnToPath` (which
 * rejects `/auth` and unsafe targets). Without this, the session would be held
 * forever on a route that never admits it (Codex P1, PR #3256).
 */
export function captureSsoCallback(location: SsoCallbackLocation): string | null {
  const fragment = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  const accessToken = new URLSearchParams(fragment).get('access_token');
  if (!accessToken || !isSamlAccessToken(accessToken)) {
    return null;
  }
  // A step-up return is exempt from the initial-callback deferral and is
  // resumed by Index without a publish, so recording it would never settle
  // (Codex P1, PR #3256 R1). Its own admission gap is ENT-4725.
  if (isStepUpReassertionReturn(location.search)) {
    return null;
  }
  pending = { accessToken, sessionId: readAccessTokenSessionId(accessToken) };
  notify();

  if (location.pathname === '/') {
    return null;
  }
  const params = new URLSearchParams({ sso_return: '1', return_to: `${location.pathname}${location.search}` });
  return `/?${params.toString()}${location.hash}`;
}

/** Test/compat helper: capture a callback fragment that landed on `/`. */
export function captureSsoCallbackFromHash(hash: string, search = ''): void {
  captureSsoCallback({ pathname: '/', search, hash });
}

/** True when `session` is the boot SSO callback session still awaiting admission. */
export function isPendingSsoCallbackSession(session: SessionLike | null | undefined): boolean {
  const accessToken = session?.access_token;
  if (!pending || typeof accessToken !== 'string' || accessToken.length === 0) {
    return false;
  }
  if (accessToken === pending.accessToken) {
    return true;
  }
  return pending.sessionId !== null && readAccessTokenSessionId(accessToken) === pending.sessionId;
}

/** True while a boot SSO callback is waiting for its admission to be published. */
export function hasPendingSsoCallbackAdmission(): boolean {
  return pending !== null;
}

/** The claim admitted the callback session (or it was discarded): stop deferring. */
export function settleSsoCallbackAdmission(): void {
  if (!pending) {
    return;
  }
  pending = null;
  notify();
}

export function subscribeSsoCallbackAdmission(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetSsoCallbackAdmissionForTests(): void {
  pending = null;
  notify();
}

if (typeof window !== 'undefined' && typeof window.location?.hash === 'string') {
  const rehome = captureSsoCallback(window.location);
  if (rehome !== null) {
    // Runs at bundle evaluation, before the router reads the location, and
    // keeps the fragment so supabase-js still consumes the session.
    window.history.replaceState(window.history.state, '', rehome);
  }
}
